/**
 * Mappers for converting Hyperliquid data to Infinex position balance format
 */

import type {
  ChainAddressId,
  PositionUserBalance,
} from '@infinex/asset-config';
import type { ChainKey } from '@infinex/chain-config';

import type { MarginSummary } from './types';

/**
 * Maps Hyperliquid account value to a single PositionUserBalance
 *
 * @param marginSummary - The margin summary containing account value
 * @param walletAddress - The wallet address owning this account
 * @returns Single PositionUserBalance representing the Hyperliquid account
 */
export function mapHyperliquidAccountToBalance(
  marginSummary: MarginSummary | null | undefined,
  walletAddress: string
): PositionUserBalance[] {
  if (!marginSummary || !marginSummary.accountValue) return [];

  // Use account value as the balance
  const accountValue = marginSummary.accountValue;

  return [
    {
      // Required ID field
      id: 'arbitrum_native' as ChainAddressId, // ChainAddressId format

      // Chain and address info
      chain: 'arbitrum' as ChainKey, // Hyperliquid runs on Arbitrum
      walletAddress,
      address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831', // Using 'native' as a synthetic address for the account

      // Position-specific data
      type: 'position' as const,
      balance: accountValue.toString(), // Account value as balance

      // Metadata
      name: 'USDC',
      symbol: 'USDC',
      decimals: 6, // USDC has 6 decimals

      // Position details
      positionData: {
        positionType: 'collateral' as const, // Using 'hyperliquid' for perps account
        protocolId: 'hyperliquid',
        externalIntegrationId: 'hyperliquid-account',

        // Store additional margin data in receiptTokenAddress as JSON
        receiptTokenAddress: JSON.stringify({
          accountValue: marginSummary.accountValue,
          totalNtlPos: marginSummary.totalNtlPos,
          totalRawUsd: marginSummary.totalRawUsd,
          totalMarginUsed: marginSummary.totalMarginUsed,
        }),
        receiptTokenType: 'token' as const,
      },

      // Official list metadata
      isOfficial: true,
      groupId: { assetId: 'usd_coin_base_bridged' },
      assetId: 'usd_coin_base_bridged',

      // Image URL for the position
      imgUrl: 'https://infinex.xyz/tokens/usd_coin.svg',
    } as PositionUserBalance,
  ];
}
